// /embed/v1/records on docs-api (this origin, the BFF session cookie and
// CSRF of the shared client, request.js) — with the shared client's key
// casing turned off: snake_case end to end, because titles are keyed by
// language tag (`uz-Cyrl`) and camelizing would rewrite those keys.
//
// Every row is named by its uuid: paths (`/plans/{plan_uuid}`), bodies
// (`retention_class_uuid`, `case_uuids`, `file_uuid`) and answers (`uuid`,
// `<thing>_uuid`) — the backend's "uuids only".

import axios from 'axios';
import { useEffect, useState } from 'react';
import baseAxios from '@/shared/utils/request.js';
import { apiErrorMessage, parseApiError } from '@/shared/utils/apiError.js';

const BASE = '/embed/v1/records';

// The shared client minus its humps transforms: JSON as the server writes it.
const AS_IS = { transformRequest: axios.defaults.transformRequest, transformResponse: axios.defaults.transformResponse };

/** `{success, response, status}` or `{success: false, error, errorInfo, message, status}`; never throws. */
async function call(method, url, { data, params } = {}) {
  try {
    const res = await baseAxios({ method, url, data, params, ...AS_IS });
    return { success: true, response: res.data, status: res.status };
  } catch (e) {
    const errorInfo = parseApiError(e);
    return { success: false, error: e.response, errorInfo, message: apiErrorMessage(errorInfo), status: e.response?.status };
  }
}

const get = (url, params) => call('GET', url, { params });
const post = (url, data) => call('POST', url, { data });
const patch = (url, data) => call('PATCH', url, { data });
const put = (url, data) => call('PUT', url, { data });
const del = (url) => call('DELETE', url);

export const records = {
  access: () => get(`${BASE}/access`),
  catalogue: () => get(`${BASE}/catalogue`),
  plans: () => get(`${BASE}/plans`),
  plan: (planUuid) => get(`${BASE}/plans/${planUuid}`),
  createPlan: (data) => post(`${BASE}/plans`, data),
  updatePlan: (planUuid, data) => patch(`${BASE}/plans/${planUuid}`, data),
  deletePlan: (planUuid) => del(`${BASE}/plans/${planUuid}`),
  approve: (planUuid) => post(`${BASE}/plans/${planUuid}/approve`),
  document: (planUuid, data) => post(`${BASE}/plans/${planUuid}/document`, data),
  addCase: (planUuid, data) => post(`${BASE}/plans/${planUuid}/cases`, data),
  updateCase: (caseUuid, data) => patch(`${BASE}/cases/${caseUuid}`, data),
  deleteCase: (caseUuid) => del(`${BASE}/cases/${caseUuid}`),
  closePlan: (planUuid) => post(`${BASE}/plans/${planUuid}/close`),

  registers: () => get(`${BASE}/registers`),
  createRegister: (data) => post(`${BASE}/registers`, data),
  updateRegister: (registerUuid, data) => patch(`${BASE}/registers/${registerUuid}`, data),
  deleteRegister: (registerUuid) => del(`${BASE}/registers/${registerUuid}`),
  // GET ?format=xlsx|csv&year= — bytes, fetched with fetchAuthedBlob.
  registerExport: (registerUuid) => `${BASE}/registers/${registerUuid}/export`,
  // params: register_uuid, q, year, date_from, date_to, status, file_uuid, page, size
  registrations: (params) => get(`${BASE}/registrations`, params),
  register: (data) => post(`${BASE}/registrations`, data),
  updateRegistration: (registrationUuid, data) => patch(`${BASE}/registrations/${registrationUuid}`, data),
  cancelRegistration: (registrationUuid, reason) => post(`${BASE}/registrations/${registrationUuid}/cancel`, { reason }),

  // params: year, plan_uuid, status, class_uuid, q
  cases: (params) => get(`${BASE}/cases`, params),
  openCases: () => get(`${BASE}/cases/open`),
  caseDetail: (caseUuid) => get(`${BASE}/cases/${caseUuid}`),
  fileDocument: (caseUuid, fileUuid) => post(`${BASE}/cases/${caseUuid}/records`, { file_uuid: fileUuid }),
  fileFolder: (caseUuid, folderUuid, scope) => post(`${BASE}/cases/${caseUuid}/file-folder`, { folder_uuid: folderUuid, scope }),
  // folderUuid null unlinks.
  linkFolder: (caseUuid, folderUuid) => put(`${BASE}/cases/${caseUuid}/folder`, { folder_uuid: folderUuid }),
  closeCase: (caseUuid) => post(`${BASE}/cases/${caseUuid}/close`),
  reopenCase: (caseUuid, reason) => post(`${BASE}/cases/${caseUuid}/reopen`, { reason }),
  withdraw: (itemUuid, reason) => post(`${BASE}/items/${itemUuid}/withdraw`, { reason }),
  whereFiled: (fileUuid) => get(`${BASE}/files/${fileUuid}`),

  review: () => get(`${BASE}/review`),
  extend: (caseUuid, until, reason) => post(`${BASE}/cases/${caseUuid}/extend`, { until, reason }),
  hold: (caseUuid, reason) => post(`${BASE}/cases/${caseUuid}/hold`, { reason }),
  release: (caseUuid, reason) => post(`${BASE}/cases/${caseUuid}/release`, { reason }),
  acts: () => get(`${BASE}/acts`),
  createAct: (caseUuids, note, kind = 'destruction') => post(`${BASE}/acts`, { case_uuids: caseUuids, note: note || null, kind }),
  transferQueue: () => get(`${BASE}/transfer`),
  packageLink: (actUuid) => get(`${BASE}/acts/${actUuid}/package`),
  rebuildPackage: (actUuid) => post(`${BASE}/acts/${actUuid}/package`),
  inventory: (data) => post(`${BASE}/inventory`, data),
  integrity: () => get(`${BASE}/integrity`),
  removeActCase: (actUuid, caseUuid) => del(`${BASE}/acts/${actUuid}/cases/${caseUuid}`),
  actDocument: (actUuid, language) => post(`${BASE}/acts/${actUuid}/document`, { language }),
  approveAct: (actUuid) => post(`${BASE}/acts/${actUuid}/approve`),
  cancelAct: (actUuid, reason) => post(`${BASE}/acts/${actUuid}/cancel`, { reason }),
  executeAct: (actUuid, confirm) => post(`${BASE}/acts/${actUuid}/execute`, { confirm }),

  // The install's people (the share dialog's directory), for a card's
  // executor: `[{uuid, person_id, display_name, email, avatar_url}]`.
  people: (search) => get('/embed/v1/share/all-users', search ? { search } : {}),
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A uuid from a URL (`?plan=`, `:id`), lower-cased; null for anything else. */
export const uuidParam = (value) => (UUID.test(String(value ?? '')) ? String(value).toLowerCase() : null);

const NONE = {
  enabled: false, can_read: false, can_file: false, can_manage: false, can_review: false, can_dispose: false,
  plan_approval: 'manual', who_files: 'records',
};
let cached = null;

// Whether the Records area exists for this person — asked once per page
// load (the answer changes only when an admin flips the policy or a grant).
export function useRecordsAccess() {
  const [access, setAccess] = useState(cached);
  useEffect(() => {
    if (cached) return undefined;
    let cancelled = false;
    records.access().then((res) => {
      cached = res.success ? { ...NONE, ...res.response } : NONE;
      if (!cancelled) setAccess(cached);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return access;
}

export function forgetRecordsAccess() {
  cached = null;
}

export const showsRecords = (access) => Boolean(access?.enabled && access?.can_read);

export const canRegister = (access) => Boolean(access?.enabled && access?.can_file);

// Whether this person may file `node` into a case: records access, or — where
// the Records policy lets editors file — edit access to the document.
// Folders: the server checks each document inside.
export function canFileNode(access, node, { canEdit = false } = {}) {
  if (!access?.enabled || node?.filedAt || node?.filed_at) return false;
  return Boolean(access.can_file || (access.who_files === 'editors' && canEdit));
}

// Whether the review (due cases, destruction acts) is this person's business.
export const showsReview = (access) => Boolean(access?.enabled && (access.can_review || access.can_dispose));
